import React from 'react'
import maplibregl from 'maplibre-gl'
import { useGlobalState } from 'state'
import hinanAreaJson from 'assets/hinan/hinanArea.json'
import { grey, red } from '@mui/material/colors'
import { IDs } from 'common/consts'
import { getPolygonCenter } from 'common/utils'
import { LAYERS } from 'common/layers'

export const HinanAreaNameLayer = () => {
  const { app, map, mapbox } = useGlobalState()

  const sourceId = IDs.school.nameLayer
  const nameRedLayerId = `${sourceId}_red`
  const nameBlackLayerId = `${sourceId}_black`

  const popupRef = React.useRef<maplibregl.Popup | null>(null)

  const getPopup = () => {
    if (!popupRef.current) {
      popupRef.current = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: false
      })
    }
    return popupRef.current
  }

  React.useEffect(() => {
    if (!mapbox) return
    addLayers()
    return () => {
      removeLayers()
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
    }
  }, [mapbox])

  React.useEffect(() => {
    removeLayers()
    addLayers()
  }, [app.hinanData])

  React.useEffect(() => {
    if (!app.estimatedFloodingLayers.includes(LAYERS.SCHOOL_AREA_RISK.id)) {
      const popup = getPopup()
      popup.remove()
    }
  }, [app.estimatedFloodingLayers])

  const clickListener = (e: any) => {
    if (!mapbox) return
    if (e.features.length === 0) return
    const areaName = e.features[0].properties.NAME
    const targets = app.hinanData[areaName]
    if (!targets) return
    const contents = targets.map((target: any) => {
      return `<div>${target.reasonType} (${target.announceType})</div>`
    })
    const html = `<div style="padding-top: 8px">${contents.join('\n')}</div>`
    const popup = getPopup()
    if (popup.isOpen()) {
      popup.setHTML(html)
      popup.setLngLat(e.lngLat)
    } else {
      popup
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(mapbox)
    }
  }

  const mouseEnterListener = () => {
    if (!mapbox) return
    mapbox.getCanvas().style.cursor = 'pointer'
  }

  const mouseLeaveListener = () => {
    if (!mapbox) return
    mapbox.getCanvas().style.cursor = ''
  }

  const addLayers = async () => {
    if (!mapbox) return
    if (mapbox.getSource(sourceId)) {
      return
    }

    const targetNames = Object.keys(app.hinanData)
    const targetSchools = hinanAreaJson.features.map((feature: any) => {
      return {
        name: feature.properties.NAME,
        risk: targetNames.includes(feature.properties.NAME)
      }
    })

    const getSchoolNameFilter = (color: 'red' | 'black') => {
      const filtered = targetSchools.filter(school => school.risk === (color === 'red'))
      if (filtered.length === 0) return ['any', ['==', ['get', 'NAME'], 'dammy']]
      const filter: any = ['any']
      filtered.forEach(school => {
        filter.push(['==', ['get', 'NAME'], school.name])
      })
      return filter
    }

    const features = hinanAreaJson.features.map((feature: any) => {
      const target = JSON.parse(JSON.stringify(feature))
      target.geometry = {
        type: 'Point',
        coordinates: getPolygonCenter(feature.geometry.type === 'MultiPolygon' ? feature.geometry.coordinates[0] : feature.geometry.coordinates)
      }
      return target
    })

    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features
      }
    })

    map.addLayer({
      id: nameRedLayerId,
      type: 'symbol',
      source: sourceId,
      minzoom: 13,
      layout: {
        'text-field': ['get', 'NAME'],
        'text-size': 20,
        'text-anchor': 'top',
        'text-offset': [0, 1],
        'text-allow-overlap': true
      },
      paint: {
        'text-color': red[700],
        'text-halo-color': 'white',
        'text-halo-width': 2,
        'text-halo-blur': 1
      },
      filter: getSchoolNameFilter('red')
    })
    map.addLayer({
      id: nameBlackLayerId,
      type: 'symbol',
      source: sourceId,
      minzoom: 13,
      layout: {
        'text-field': ['get', 'NAME'],
        'text-size': 20,
        'text-anchor': 'top',
        'text-offset': [0, 1],
        'text-allow-overlap': true
      },
      paint: {
        'text-color': grey[900],
        'text-halo-color': 'white',
        'text-halo-width': 2,
        'text-halo-blur': 1
      },
      filter: getSchoolNameFilter('black')
    })

    mapbox.on('click', nameRedLayerId, clickListener)
    mapbox.on('mouseenter', nameRedLayerId, mouseEnterListener)
    mapbox.on('mouseleave', nameRedLayerId, mouseLeaveListener)
  }

  const removeLayers = () => {
    mapbox?.off('click', nameRedLayerId, clickListener)
    mapbox?.off('mouseenter', nameRedLayerId, mouseEnterListener)
    mapbox?.off('mouseleave', nameRedLayerId, mouseLeaveListener)
    const popup = getPopup()
    popup.remove()

    map.removeLayer(nameRedLayerId)
    map.removeLayer(nameBlackLayerId)
    map.removeSource(sourceId)
  }

  return <></>
}
